import { Box, Typography } from '@mui/material';
import { useEffect } from 'react';

import { Button, ModalContentContainer, TextField } from '@/components';
import { isEmailValid, useForm } from '@/hooks';
import {
  selectActiveModalParams,
  uiActions,
  useAddOrganizationMembersMutation,
  useAppDispatch,
  useAppSelector,
  useUpdateOrganizationNameMutation,
} from '@/store';
import { SnackbarUtils } from '@/utils';

export const UpgradeTeamModalContent = () => {
  const dispatch = useAppDispatch();
  const { organizationName, seatCap, usedSeats, organizationId } =
    useAppSelector(selectActiveModalParams) || {};

  const remainingSeats =
    seatCap && usedSeats ? Math.min(seatCap - usedSeats, 3) : undefined;

  const { form, setForm, onChange, errors, setErrors } = useForm<{
    companyName: string;
    teamMembers: { fullName: string; email: string }[];
  }>({
    companyName: organizationName || '',
    teamMembers: remainingSeats
      ? Array(remainingSeats).fill({ fullName: '', email: '' })
      : [
          { fullName: '', email: '' },
          { fullName: '', email: '' },
          { fullName: '', email: '' },
        ],
  });

  useEffect(() => {
    if (organizationName) {
      setForm(prev => ({
        ...prev,
        companyName: organizationName,
      }));
    }
  }, [organizationName, setForm]);

  useEffect(() => {
    if (remainingSeats && remainingSeats > 0) {
      setForm(prev => ({
        ...prev,
        teamMembers: Array(remainingSeats).fill({ fullName: '', email: '' }),
      }));
    }
  }, [remainingSeats, setForm]);

  const [addNewTeamMembers, { isLoading: addNewTeamMembersLoading }] =
    useAddOrganizationMembersMutation();
  const [updateOrganizationName, { isLoading: updateOrganizationNameLoading }] =
    useUpdateOrganizationNameMutation();

  const onSubmit = async () => {
    if (!form.companyName) {
      setErrors({
        ...errors,
        companyName: 'Company name is required',
      });
      return;
    } else if (
      form.teamMembers.some(
        member => !isEmailValid(member.email) && member.fullName !== '',
      )
    ) {
      const invalidIndex = form.teamMembers.findIndex(
        member => !isEmailValid(member.email) && member.fullName !== '',
      );
      setErrors({
        ...errors,
        teamMembers: invalidIndex,
      });
      return;
    }
    try {
      await updateOrganizationName({
        organizationId: organizationId!,
        organizationName: form.companyName,
      }).unwrap();
      if (form.teamMembers.some(member => isEmailValid(member.email))) {
        await addNewTeamMembers({
          newMembers: form.teamMembers
            .filter(member => isEmailValid(member.email))
            .map(member => ({
              email: member.email,
              name: member.fullName,
            })),
          sendInvite: true,
        }).unwrap();
      }
      SnackbarUtils.success('Team upgraded successfully!');
      dispatch(uiActions.closeActiveModal());
      const url = new URL(window.location.href);
      url.searchParams.delete('checkout_session_id');
      window.history.replaceState({}, '', url);
    } catch {
      SnackbarUtils.error(
        'Failed to finalize team upgrade. Please contact support.',
      );
    }
  };

  return (
    <ModalContentContainer stretch>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}>
        <Typography variant="h1">Create your team</Typography>
        <TextField
          id="companyName"
          label="Team Name"
          fullWidth={false}
          placeholder="Company Name"
          sx={{
            width: 300,
            mt: 2,
          }}
          value={form.companyName}
          onChange={onChange}
          error={!!errors.companyName}
          helperText={errors.companyName || ''}
        />
        <Typography variant="body2" sx={{ mt: 3 }}>
          Start by adding a few team members. You can invite additional members
          later.
        </Typography>
        {form.teamMembers.map((member, index) => (
          <Box
            key={index}
            sx={{
              mt: index === 0 ? 3 : 1,
              display: 'flex',
              flexDirection: 'row',
              width: 500,
            }}>
            <Box sx={{ width: '50%' }}>
              <TextField
                placeholder="Full Name"
                sx={{ mr: 1 }}
                value={member.fullName}
                onChange={e =>
                  setForm(prev => {
                    const newMembers = [...prev.teamMembers];
                    newMembers[index] = {
                      ...newMembers[index],
                      fullName: e.target.value,
                    };
                    return {
                      ...prev,
                      teamMembers: newMembers,
                    };
                  })
                }
                error={errors.teamMembers === index && member.fullName === ''}
                helperText={
                  errors.teamMembers === index && member.fullName === ''
                    ? 'Please enter a full name'
                    : ''
                }
              />
            </Box>
            <Box sx={{ width: '50%' }}>
              <TextField
                key={index}
                placeholder="email@example.com"
                sx={{ ml: 1 }}
                value={member.email}
                onChange={e =>
                  setForm(prev => {
                    const newMembers = [...prev.teamMembers];
                    newMembers[index] = {
                      ...newMembers[index],
                      email: e.target.value,
                    };
                    return {
                      ...prev,
                      teamMembers: newMembers,
                    };
                  })
                }
                error={
                  errors.teamMembers === index && !isEmailValid(member.email)
                }
                helperText={
                  errors.teamMembers === index && !isEmailValid(member.email)
                    ? 'Please enter a valid email address'
                    : ''
                }
              />
            </Box>
          </Box>
        ))}
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
        }}>
        <Button
          variant="primary"
          label="Create Team"
          skinny={false}
          fullWidth={false}
          sx={{ mt: 5 }}
          onClick={onSubmit}
          isLoading={addNewTeamMembersLoading || updateOrganizationNameLoading}
        />
      </Box>
    </ModalContentContainer>
  );
};
